import type { IntakeQuestionnaire, IntakeAnswers, ValidationError } from './types';

const FIELD_KEY_RE = /^[a-z][a-z0-9_]*$/;

let errorCounter = 0;
function makeId(): string {
  return `ve-${++errorCounter}`;
}

export function validateQuestionnaire(q: IntakeQuestionnaire): ValidationError[] {
  const errors: ValidationError[] = [];
  const seenKeys = new Map<string, string>();
  errorCounter = 0;

  for (const section of q.sections) {
    // Section title required
    if (!section.title.trim()) {
      errors.push({
        id: makeId(),
        type: 'error',
        message: 'Section title is required',
        sectionId: section.id,
        field: 'title',
      });
    }

    for (const question of section.questions) {
      // Question label required
      if (!question.label.trim()) {
        errors.push({
          id: makeId(),
          type: 'error',
          message: 'Question label is required',
          questionId: question.id,
          sectionId: section.id,
          field: 'label',
        });
      }

      // fieldKey must be snake_case
      if (!FIELD_KEY_RE.test(question.fieldKey)) {
        errors.push({
          id: makeId(),
          type: 'error',
          message: `Field key "${question.fieldKey}" must be snake_case (lowercase letters, numbers, underscores; start with letter)`,
          questionId: question.id,
          sectionId: section.id,
          field: 'fieldKey',
        });
      }

      // No duplicate fieldKeys
      if (seenKeys.has(question.fieldKey)) {
        errors.push({
          id: makeId(),
          type: 'error',
          message: `Duplicate field key: "${question.fieldKey}"`,
          questionId: question.id,
          sectionId: section.id,
          field: 'fieldKey',
        });
      } else {
        seenKeys.set(question.fieldKey, question.id);
      }

      // select / multi-select must have options
      if (
        (question.type === 'select' || question.type === 'multi-select') &&
        (!question.options || question.options.length === 0)
      ) {
        errors.push({
          id: makeId(),
          type: 'error',
          message: 'Select/multi-select must have at least 1 option',
          questionId: question.id,
          sectionId: section.id,
          field: 'options',
        });
      }

      // Option-level validation (for select / multi-select with options)
      if (
        (question.type === 'select' || question.type === 'multi-select') &&
        question.options &&
        question.options.length > 0
      ) {
        const seenValues = new Set<string>();

        for (const opt of question.options) {
          if (!opt.label.trim()) {
            errors.push({
              id: makeId(),
              type: 'error',
              message: `Option in "${question.label || question.fieldKey}" has an empty label`,
              questionId: question.id,
              sectionId: section.id,
              field: 'options',
            });
          }
          if (!opt.value.trim()) {
            errors.push({
              id: makeId(),
              type: 'error',
              message: `Option in "${question.label || question.fieldKey}" has an empty value`,
              questionId: question.id,
              sectionId: section.id,
              field: 'options',
            });
          }
          if (opt.value.trim() && seenValues.has(opt.value)) {
            errors.push({
              id: makeId(),
              type: 'error',
              message: `Duplicate option value "${opt.value}" in "${question.label || question.fieldKey}"`,
              questionId: question.id,
              sectionId: section.id,
              field: 'options',
            });
          } else {
            seenValues.add(opt.value);
          }
        }
      }
    }
  }

  return errors;
}

// ─── Missing Required Fields Helper ───

export interface MissingField {
  sectionTitle: string;
  questionLabel: string;
  fieldKey: string;
}

export function getMissingRequired(q: IntakeQuestionnaire, answers: IntakeAnswers): MissingField[] {
  const missing: MissingField[] = [];

  for (const section of q.sections) {
    for (const question of section.questions) {
      if (!question.required) continue;

      const val = answers[question.fieldKey];
      const isEmpty = val === undefined || val === '' || (Array.isArray(val) && val.length === 0);

      if (isEmpty) {
        missing.push({
          sectionTitle: section.title,
          questionLabel: question.label,
          fieldKey: question.fieldKey,
        });
      }
    }
  }

  return missing;
}
